import axiosInstance from "@/api/axiosConfig";

import type {
  BriefingResponse,
  ClientDashboard,
  DashboardScope,
  PracticeDashboard,
  RecommendationsResponse
} from "../types";

// `axiosInstance.baseURL` already ends in `/api`, so paths here omit it.
const BASE = "/dashboard";

const scopeBase = (scope: DashboardScope): string =>
  scope.kind === "client"
    ? `${BASE}/client/${encodeURIComponent(scope.id)}`
    : `${BASE}/practice`;

/** `refresh` bypasses the server's cached payload and recomputes it. */
export type SummaryOptions = { refresh?: boolean };

const summaryParams = (windowDays: number, opts?: SummaryOptions) => ({
  window_days: windowDays,
  ...(opts?.refresh ? { refresh: 1 } : {})
});

export const DashboardAPI = {
  async getPractice(
    windowDays = 14,
    opts?: SummaryOptions
  ): Promise<PracticeDashboard> {
    const { data } = await axiosInstance.get<PracticeDashboard>(
      `${BASE}/practice`,
      { params: summaryParams(windowDays, opts) }
    );
    return data;
  },

  async getClient(
    foreignNationalId: string,
    windowDays = 14,
    opts?: SummaryOptions
  ): Promise<ClientDashboard> {
    const { data } = await axiosInstance.get<ClientDashboard>(
      `${BASE}/client/${encodeURIComponent(foreignNationalId)}`,
      { params: summaryParams(windowDays, opts) }
    );
    return data;
  },

  /** The scoped summary for either dashboard. */
  async getSummary(
    scope: DashboardScope,
    windowDays = 14,
    opts?: SummaryOptions
  ): Promise<PracticeDashboard | ClientDashboard> {
    return scope.kind === "client"
      ? DashboardAPI.getClient(scope.id, windowDays, opts)
      : DashboardAPI.getPractice(windowDays, opts);
  },

  /** Rules plus cached LLM chips; `refresh` forces the model to run again. */
  async getRecommendations(
    scope: DashboardScope,
    opts?: { refresh?: boolean }
  ): Promise<RecommendationsResponse> {
    const { data } = await axiosInstance.get<RecommendationsResponse>(
      `${scopeBase(scope)}/recommendations`,
      { params: opts?.refresh ? { refresh: 1 } : undefined }
    );
    return data;
  },

  /** The practice briefing; `refresh` asks the model to write it again. */
  async getBriefing(opts?: { refresh?: boolean }): Promise<BriefingResponse> {
    const { data } = await axiosInstance.get<BriefingResponse>(
      `${BASE}/practice/briefing`,
      { params: opts?.refresh ? { refresh: 1 } : undefined }
    );
    return data;
  }
};
